import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Box, Button, Chip, SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CLEAR_FILTERS_LABEL, FILTER_CHIP_HEIGHT, getShipsFromSupplierChipLabel } from '../../../constants/marketplace';
import { HIDDEN_SCROLLBAR_SX, HOVER_MEDIA, PILL_BORDER_RADIUS } from '../../../constants/ui';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';
import { marketplaceSetFiltersAction, marketplaceSetSupplierFilterAction } from '../../../store/actions/marketplace';
import {
  selectCanFilterBySupplier,
  selectHasActiveMarketplaceFilters,
  selectIsSupplierFilterOn,
  selectMarketplaceCategories,
  selectMarketplaceFilters,
  selectSupplierLock,
} from '../../../store/selectors/marketplace';
import { IMarketplaceCategory } from '../../../types/marketplace';

// Active chips fill to a primary tint with a primary border rather than the solid primary.
const chipSx: SxProps<Theme> = {
  height: FILTER_CHIP_HEIGHT,
  borderRadius: PILL_BORDER_RADIUS,
  fontWeight: 400,
  backgroundColor: 'background.paper',
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: 'action.hover' },
  },
  '&.MuiChip-filledPrimary': {
    border: 1,
    borderColor: ({ palette }) => alpha(palette.primary.main, 0.4),
    backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity),
    color: 'primary.main',
    [HOVER_MEDIA]: {
      '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.2) },
    },
  },
};

// Scrolls sideways instead of wrapping so the toolbar keeps one chip row; chips must not shrink.
const railSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 1,
  overflowX: 'auto',
  py: 0.25,
  '& > *': { flexShrink: 0 },
  ...HIDDEN_SCROLLBAR_SX,
};

const supplierChipSx: SxProps<Theme> = {
  ...chipSx,
  '& .MuiChip-icon': { fontSize: 16, ml: 1.25, color: 'action.active' },
  '&.MuiChip-filledPrimary .MuiChip-icon': { color: 'primary.main' },
};

const chipDividerSx: SxProps<Theme> = { width: '1px', height: 24, bgcolor: 'divider' };

const supplierChipIcon = <LocalShippingOutlinedIcon />;

const clearButtonSx: SxProps<Theme> = {
  height: FILTER_CHIP_HEIGHT,
  px: 1.25,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

interface ICategoryChipProps {
  category: IMarketplaceCategory;
  isActive: boolean;
  onToggle: (categoryId: string) => void;
}

const CategoryChip: React.FC<ICategoryChipProps> = ({ category, isActive, onToggle }) => {
  const handleClick = useCallback(() => onToggle(category.id), [onToggle, category.id]);

  return (
    <Chip
      label={category.name}
      variant={isActive ? 'filled' : 'outlined'}
      color={isActive ? 'primary' : 'default'}
      onClick={handleClick}
      aria-pressed={isActive}
      sx={chipSx}
    />
  );
};

export const MarketplaceCategoryFilters: React.FC = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectMarketplaceFilters);
  const categories = useSelector(selectMarketplaceCategories);
  const hasActiveFilters = useSelector(selectHasActiveMarketplaceFilters);
  const supplierLock = useSelector(selectSupplierLock);
  const isSupplierFilterOn = useSelector(selectIsSupplierFilterOn);
  const canFilterBySupplier = useSelector(selectCanFilterBySupplier);
  const railRef = useRef<HTMLDivElement>(null);

  useHorizontalWheelScroll(railRef);

  const isAllActive = !filters.category;

  const supplierChipLabel = useMemo(() => getShipsFromSupplierChipLabel(supplierLock?.name), [supplierLock?.name]);

  const handleClearAll = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ query: '', category: '', fitsBudget: false }));
    dispatch(marketplaceSetSupplierFilterAction(false));
  }, [dispatch]);

  const handleSupplierToggle = useCallback(() => {
    dispatch(marketplaceSetSupplierFilterAction(!isSupplierFilterOn));
  }, [dispatch, isSupplierFilterOn]);

  const handleSelectAll = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ category: '' }));
  }, [dispatch]);

  // Single select: the catalog endpoint filters on one category, so a new pick replaces the last.
  const handleCategoryToggle = useCallback(
    (categoryId: string) => {
      dispatch(marketplaceSetFiltersAction({ category: filters.category === categoryId ? '' : categoryId }));
    },
    [dispatch, filters.category],
  );

  return (
    <Box ref={railRef} sx={railSx}>
      {canFilterBySupplier && (
        <>
          <Chip
            icon={supplierChipIcon}
            label={supplierChipLabel}
            variant={isSupplierFilterOn ? 'filled' : 'outlined'}
            color={isSupplierFilterOn ? 'primary' : 'default'}
            onClick={handleSupplierToggle}
            aria-pressed={isSupplierFilterOn}
            sx={supplierChipSx}
          />
          <Box aria-hidden sx={chipDividerSx} />
        </>
      )}
      <Chip
        label="All"
        variant={isAllActive ? 'filled' : 'outlined'}
        color={isAllActive ? 'primary' : 'default'}
        onClick={handleSelectAll}
        aria-pressed={isAllActive}
        sx={chipSx}
      />
      {categories.map((category) => (
        <CategoryChip
          key={category.id}
          category={category}
          isActive={filters.category === category.id}
          onToggle={handleCategoryToggle}
        />
      ))}
      {hasActiveFilters && (
        <Button variant="text" color="primary" onClick={handleClearAll} sx={clearButtonSx}>
          {CLEAR_FILTERS_LABEL}
        </Button>
      )}
    </Box>
  );
};
